import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    PLATFORM_ID,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core';
import { UpdateUserDto } from '@core/dto';
import {
    ApiClientInformation,
    ApiDealerInformation,
    DealerPatchBody,
    DealerPatchUserBody,
} from '@core/services/dealer/dealer.interface';
import { DealerService } from '@core/services/dealer/dealer.service';
import { MetadataRecord } from '@core/services/licenses/licenses.interfaces';
import { LicensesService } from '@core/services/licenses/licenses.service';
import { UsersService } from '@core/services/users/users.service';
import { UILicense } from '@features/licenses/interfaces/ui-license-table.interface';
import { Button } from '@ntv360/component-pantry';
import {
    CopyDirective,
    NtvTableCellDirective,
    TableComponent,
    TableToolbarComponent,
} from '@shared';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { SVG_ICONS } from '@shared/constants';
import { TableToolbarButton } from '@shared/interfaces';
import { citySelectionKeyToApiCityName, Logger } from '@shared/utils';
import isEqual from 'lodash/isEqual';
import { EMPTY, finalize, forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import {
    SINGLE_DEALER_LICENSE_SEARCH_FIELDS,
    SINGLE_DEALER_LICENSE_TABLE_COLUMNS,
} from '../../constants/single-dealer-license-table.columns';
import { EditDealerModalComponent } from '../edit-dealer-modal/edit-dealer-modal.component';
import {
    DEALER_CHANGE_PASSWORD_MIN_COOLDOWN_SECONDS,
    DEALER_PASSWORD_CHANGE_LIMIT,
    DEALER_SAVE_MESSAGES,
    DEALER_UI_LABELS,
    EMPTY_DEALER_HYDRATION,
    SINGLE_DEALER_LICENSE_PAGE_SIZE,
    SPRITE_PATH,
} from './single-dealer.constants';
import {
    DealerEditHydration,
    DealerEditSubmitPayload,
    DealerLoadCallbacks,
    LicenseActionEvent,
} from './single-dealer.interfaces';
import {
    clearDealerChangePasswordCooldown,
    dealerChangePasswordCooldownSecondsLeft,
    formatCooldownTime,
    getCompactPhone,
    isDealerPasswordChangeLimitError,
    normalizeString,
    setDealerChangePasswordCooldown,
} from './single-dealer.utils';

/**
 * @public
 * @description Single-dealer screen: header, licenses table, edit modal, and dealer-targeted change-password with per-dealer cooldown.
 */
@Component({
    selector: 'app-single-dealer',
    imports: [
        Button,
        TableComponent,
        TableToolbarComponent,
        NtvTableCellDirective,
        CopyDirective,
        EditDealerModalComponent,
        ConfirmationModalComponent,
    ],
    templateUrl: './single-dealer.component.html',
    styleUrl: './single-dealer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleDealerComponent {
    /** Angular router for navigation */
    private readonly router = inject(Router);
    /** Active route for accessing route parameters */
    private readonly route = inject(ActivatedRoute);
    /** Destroy reference for cleanup and subscription management */
    private readonly destroyRef = inject(DestroyRef);
    /** Authentication service for user-related actions and cooldowns */
    private readonly authService = inject(AuthService);
    /** Service for dealer-specific data operations */
    private readonly dealerService = inject(DealerService);
    /** Service for managing and mapping licenses */
    private readonly licensesService = inject(LicensesService);
    /** Service for user management and validation */
    private readonly usersService = inject(UsersService);

    /** Browser document for full page reload after license delete */
    private readonly document = inject(DOCUMENT);
    /** Platform id for browser-only sessionStorage cooldown */
    private readonly platformId = inject(PLATFORM_ID);

    /** Dealer `userId` the active countdown applies to (for clearing storage when it elapses) */
    private dealerCooldownTimerUserId: string | null = null;

    /** Path to the icons sprite file */
    protected readonly spritePath = SPRITE_PATH;

    /** Map of available SVG icon identifiers */
    protected readonly icons: Record<string, string> = SVG_ICONS;

    /** UI labels and text constants */
    protected readonly labels = DEALER_UI_LABELS;

    /** Copy for the change-password rate limit popup on the edit modal */
    protected readonly passwordLimitTitle = DEALER_PASSWORD_CHANGE_LIMIT.TITLE;
    protected readonly passwordLimitMessage = DEALER_PASSWORD_CHANGE_LIMIT.MESSAGE;

    /** Unique identifier for the client information record */
    private readonly clientInformationId = signal<string | null>(null);

    /** Current dealer data used for display and hydration of the edit form */
    protected readonly dealerHydration = signal<DealerEditHydration>({ ...EMPTY_DEALER_HYDRATION });

    /** Global loading state for the entire dealer page */
    protected readonly dealerPageLoading = signal(false);

    /** Controls visibility of the edit dealer modal */
    protected readonly isEditDealerModalOpen = signal(false);
    /** Error message to display when a save operation fails */
    protected readonly saveError = signal<string | null>(null);

    /** Primary business name for the header display */
    protected readonly headerBusinessName = signal('');
    /** Formatted dealer alias for the header display */
    protected readonly headerAlias = signal('');
    /** Formatted location string (City, State, Region) for the header display */
    protected readonly headerLocation = signal('');

    /** Signal tracking if the avatar image failed to load */
    protected readonly headerAvatarImageFailed = signal(false);

    /** Current search query for the license table (server-side via GET /api/licenses) */
    protected readonly licenseSearch = signal('');
    /** Dealer owner user id used to filter licenses (`ownerId` on the API) */
    private readonly dealerOwnerUserId = signal<string | null>(null);
    /** License rows returned by the latest API fetch */
    protected readonly licenseRows = signal<UILicense[]>([]);
    /** Total license count for the dealer (unchanged while searching) */
    private readonly dealerLicenseTotalCount = signal(0);
    /** Total rows for the license table pagination (includes active search) */
    protected readonly licenseTableTotalCount = signal(0);
    /** Current licenses API page (1-based) */
    private readonly licenseCurrentPage = signal(1);

    /** Count of licenses for the current dealer shown in the header meta card */
    protected readonly dealerLicenseCount = computed(() => this.dealerLicenseTotalCount());

    /** Loading state specific to license data fetching */
    protected readonly licensesLoading = signal(false);
    /** True while appending the next page of licenses */
    protected readonly licensesLoadingMore = signal(false);

    /** Column definitions passed to the license table panel */
    protected readonly licenseColumns = SINGLE_DEALER_LICENSE_TABLE_COLUMNS;
    /** Configuration for table toolbar buttons */
    protected readonly toolbarButtons = signal<TableToolbarButton[]>([]);

    /** License row targeted by the delete confirmation modal */
    protected readonly licensePendingDelete = signal<UILicense | null>(null);
    /** Controls visibility of the per-license delete confirmation modal */
    protected readonly showLicenseDeleteConfirmModal = signal(false);
    /** Controls visibility of the post-delete success modal */
    protected readonly showLicenseDeleteSuccessModal = signal(false);
    /** True while the delete request is in flight */
    protected readonly isLicenseDeleteInProgress = signal(false);

    /** True while the dealer delete request is in flight */
    protected readonly isDeletingDealer = signal(false);

    /** Controls visibility of the password reset confirmation modal */
    protected readonly showPasswordResetModal = signal(false);
    /** Message to display in the password reset confirmation modal */
    protected readonly passwordResetMessage = signal('');
    /** Rate-limit popup shown above the edit-dealer modal */
    protected readonly showPasswordLimitModal = signal(false);

    /** Controls visibility of the save success confirmation modal */
    protected readonly showDealerSaveSuccessModal = signal(false);
    /** Message to display in the save success confirmation modal */
    protected readonly dealerSaveSuccessMessage = signal<string>(DEALER_SAVE_MESSAGES.DEFAULT);

    /** Controls visibility of the dealer delete success modal */
    protected readonly showDealerDeleteSuccessModal = signal(false);

    /** True while the change-password email request is in flight for this dealer */
    protected readonly isChangePasswordLoading = signal(false);
    /** Seconds remaining in the password reset cooldown period for the current dealer only */
    protected readonly changePasswordSecondsLeft = signal(0);
    /** Timer interval for counting down the cooldown */
    private changePasswordInterval: ReturnType<typeof setInterval> | null = null;

    /** Computed dealer display name from first/last name or contact person fallback */
    protected readonly headerDealerDisplayName = computed(() => {
        const h = this.dealerHydration();
        const first = normalizeString(h.firstName);
        const last = normalizeString(h.lastName);
        const fromUser = [first, last].filter(Boolean).join(' ').trim();
        if (fromUser) return fromUser;
        return normalizeString(h.contactPerson);
    });

    /** Normalized profile picture URL or null if empty */
    protected readonly dealerProfilePictureUrl = computed(() => {
        const raw = normalizeString(this.dealerHydration().profilePicture);
        return raw.length > 0 ? raw : null;
    });

    /** Accessible alt text for the dealer avatar image */
    protected readonly dealerAvatarAlt = computed(() => {
        const person = this.headerDealerDisplayName();
        if (person) return `Profile photo, ${person}`;
        const biz = this.headerBusinessName();
        return biz && biz !== '—' ? `Profile photo, ${biz}` : 'Dealer profile photo';
    });

    /** Normalized dealer email for display */
    protected readonly dealerHeaderEmail = computed(() =>
        normalizeString(this.dealerHydration().initialEmail),
    );

    /** Normalized dealer phone number for display */
    protected readonly dealerHeaderPhone = computed(() =>
        normalizeString(this.dealerHydration().contactPersonPhone),
    );

    /** RFC 3966 tel URI derived from the dealer phone number */
    protected readonly dealerHeaderTelHref = computed(() => {
        const raw = this.dealerHeaderPhone();
        return `tel:${getCompactPhone(raw)}`;
    });

    /** True when this dealer's change-password cooldown timer has a positive remaining count */
    protected readonly isChangePasswordCoolingDown = computed(
        () => this.changePasswordSecondsLeft() > 0,
    );

    /** Tooltip for the edit modal change-password button while per-dealer cooldown is active */
    protected readonly changePasswordTooltip = computed(() => {
        const seconds = this.changePasswordSecondsLeft();
        if (seconds <= 0) return '';
        return `Please wait ${formatCooldownTime(seconds)} before requesting another password reset for this dealer.`;
    });

    /** Disabled state for change password on this dealer only (other dealers are unaffected) */
    protected readonly resetPasswordDisabled = computed(
        () => this.isChangePasswordLoading() || this.isChangePasswordCoolingDown(),
    );

    /**
     * @public
     * @description Reads route id, loads dealer data, registers destroy cleanup, and resets avatar error when the profile image URL changes.
     * @returns void
     */
    constructor() {
        const id = this.route.snapshot.paramMap.get('id');
        this.clientInformationId.set(id);
        if (id) {
            this.loadDealer(id);
        }
        this.destroyRef.onDestroy(() => this.clearChangePasswordTimer());

        effect(
            () => {
                this.dealerHydration().profilePicture;
                this.headerAvatarImageFailed.set(false);
            },
            { allowSignalWrites: true },
        );
    }

    /**
     * @protected
     * @description Handles image load failures by showing the placeholder
     * @returns void
     */
    protected onHeaderAvatarImageError(): void {
        this.headerAvatarImageFailed.set(true);
    }

    /**
     * @protected
     * @description Opens the dealer edit modal
     * @returns void
     */
    protected openEditDealerModal(): void {
        this.saveError.set(null);
        this.isEditDealerModalOpen.set(true);
    }

    /**
     * @protected
     * @description Closes the dealer edit modal and clears errors
     * @returns void
     */
    protected closeEditDealerModal(): void {
        this.isEditDealerModalOpen.set(false);
        this.saveError.set(null);
        this.showPasswordLimitModal.set(false);
    }

    /**
     * @protected
     * @description Updates the license search term and refetches from GET /api/licenses.
     * @param value - Debounced search string (license key and alias)
     * @returns void
     */
    protected onLicenseSearchChange(value: string): void {
        this.licenseSearch.set(value);
        this.licenseCurrentPage.set(1);
        const userId = this.dealerOwnerUserId();
        if (userId) {
            this.loadLicenses(userId, value);
        }
    }

    /**
     * @protected
     * @description Loads the next page of licenses and appends rows to the table.
     * @returns void
     */
    protected loadMoreLicenses(): void {
        if (this.licensesLoadingMore() || this.licensesLoading()) {
            return;
        }

        const totalCount = this.licenseTableTotalCount();
        if (this.licenseRows().length >= totalCount) {
            return;
        }

        const userId = this.dealerOwnerUserId();
        if (!userId) {
            return;
        }

        const nextPage = this.licenseCurrentPage() + 1;
        this.loadLicenses(userId, this.licenseSearch(), { page: nextPage, append: true });
    }

    /**
     * @protected
     * @description Opens the single-license page when a redirectable cell (e.g. ID) is clicked.
     * @param event - Cell click payload from the license table
     * @returns void
     */
    protected onLicenseCellClick(event: {
        field: string;
        value: string | number | undefined;
        row: UILicense;
    }): void {
        if (event.row.id) {
            window.open(`/licenses/${event.row.id}`, '_blank');
        }
    }

    /**
     * @protected
     * @description Toolbar primary actions (reserved; license rows use {@link onLicenseAction}).
     * @param action - Toolbar button name from {@link TableToolbarComponent}
     * @returns void
     */
    protected onLicenseToolbarAction(action: string): void {
        void action;
    }

    /**
     * @protected
     * @description Handles the dealer update submission flow
     * @param payload - The data emitted from the edit modal
     * @returns void
     */
    protected onDealerUpdate(payload: DealerEditSubmitPayload): void {
        const userId = payload.userId;
        const clientId = payload.clientInformationId ?? this.clientInformationId();

        if (!userId || !clientId) {
            Logger.error('SingleDealerComponent', 'Missing identifiers for update');
            this.saveError.set('Missing dealer identifiers. Reload and try again.');
            return;
        }

        const emailChanged = this.hasEmailChanged(payload);
        const dealerPatch$ = this.prepareDealerPatch(payload, emailChanged);

        const finishOk = (emailChangedFlow: boolean): void => {
            this.closeEditDealerModal();
            this.subscribeDealerLoad(clientId, {
                onComplete: () => {
                    this.dealerSaveSuccessMessage.set(
                        emailChangedFlow
                            ? DEALER_SAVE_MESSAGES.EMAIL_CHANGED
                            : DEALER_SAVE_MESSAGES.DEFAULT,
                    );
                    this.showDealerSaveSuccessModal.set(true);
                },
                onRefreshFailed: () => {
                    this.dealerSaveSuccessMessage.set(DEALER_SAVE_MESSAGES.REFRESH_FAILED);
                    this.showDealerSaveSuccessModal.set(true);
                },
            });
        };

        if (emailChanged) {
            this.handleEmailChangeFlow(payload, dealerPatch$, finishOk);
            return;
        }

        this.handleStandardUpdateFlow(dealerPatch$, finishOk);
    }

    /**
     * @protected
     * @description Handles deletion of the current dealer
     * @returns void
     */
    protected onDealerDelete(): void {
        const userId = this.dealerHydration().userId;
        if (!userId) return;

        this.isDeletingDealer.set(true);
        this.dealerService
            .deleteSingleDealer(userId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isDeletingDealer.set(false)),
            )
            .subscribe({
                next: () => {
                    this.closeEditDealerModal();
                    this.showDealerDeleteSuccessModal.set(true);
                },
                error: (err: unknown) =>
                    Logger.error('SingleDealerComponent', 'Deletion failed', err),
            });
    }

    /**
     * @protected
     * @description Navigates to the dealers list after successful deletion
     * @returns void
     */
    protected async onDeleteSuccessConfirm(): Promise<void> {
        this.showDealerDeleteSuccessModal.set(false);
        await this.router.navigate(['/dealers']);
    }

    /**
     * @protected
     * @description Requests a force password reset for this dealer (`POST /api/users/:userId/force-password-change`) and starts per-dealer cooldown when TTL is positive.
     * @returns void
     */
    protected onResetPasswordForDealer(): void {
        const userId = normalizeString(this.dealerHydration().userId);
        if (!userId || this.resetPasswordDisabled()) return;

        const targetEmail =
            normalizeString(this.dealerHydration().initialEmail) || 'the dealer account email';

        this.isChangePasswordLoading.set(true);
        this.usersService
            .forcePasswordChange(userId)
            .pipe(
                switchMap(() => this.authService.getChangePasswordCooldownTtl()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: ({ ttl }) => {
                    this.isChangePasswordLoading.set(false);
                    this.passwordResetMessage.set(
                        `We sent an email to ${targetEmail} to reset the password for this dealer account. Please check that inbox to continue.`,
                    );
                    this.showPasswordResetModal.set(true);
                    const storage = this.dealerCooldownStorage();

                    const effectiveTtl = Math.max(ttl, DEALER_CHANGE_PASSWORD_MIN_COOLDOWN_SECONDS);

                    if (storage && effectiveTtl > 0) {
                        setDealerChangePasswordCooldown(storage, userId, effectiveTtl);
                        this.startChangePasswordTimer(effectiveTtl, userId);
                    }
                },
                error: (err: unknown) => {
                    this.isChangePasswordLoading.set(false);
                    if (isDealerPasswordChangeLimitError(err)) {
                        this.showPasswordLimitModal.set(true);
                        return;
                    }
                    Logger.error('SingleDealerComponent', 'Password reset failed', err);
                },
            });
    }

    /**
     * @protected
     * @description Closes the password reset confirmation modal
     * @returns void
     */
    protected closePasswordResetModal(): void {
        this.showPasswordResetModal.set(false);
    }

    /**
     * @protected
     * @description Dismisses the change-password rate limit popup on the edit modal
     * @returns void
     */
    protected closePasswordLimitModal(): void {
        this.showPasswordLimitModal.set(false);
    }

    /**
     * @protected
     * @description Dismisses the dealer save success modal
     * @returns void
     */
    protected dismissDealerSaveSuccessModal(): void {
        this.showDealerSaveSuccessModal.set(false);
    }

    /**
     * @protected
     * @description Handles license-specific actions (e.g. edit, delete)
     * @param event - The action event data
     * @returns void
     */
    protected onLicenseAction(event: LicenseActionEvent): void {
        if (event.action !== 'delete') {
            Logger.info('SingleDealerComponent', 'License action', event.action, event.row?.id);
            return;
        }
        const id = normalizeString(event.row?.id);
        if (!id) {
            Logger.warn('SingleDealerComponent', 'Delete ignored: missing license id');
            return;
        }
        this.licensePendingDelete.set(event.row);
        this.showLicenseDeleteConfirmModal.set(true);
    }

    /**
     * @protected
     * @description Closes the license delete confirmation modal without calling the API.
     * @returns void
     */
    protected closeLicenseDeleteConfirmModal(): void {
        this.showLicenseDeleteConfirmModal.set(false);
        this.licensePendingDelete.set(null);
    }

    /**
     * @protected
     * @description Deletes the selected license via the API and opens the success confirmation modal.
     * @returns void
     */
    protected onConfirmLicenseDelete(): void {
        const row = this.licensePendingDelete();
        const licenseId = normalizeString(row?.id);
        if (!licenseId) return;

        this.isLicenseDeleteInProgress.set(true);
        this.licensesService
            .delete(licenseId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isLicenseDeleteInProgress.set(false)),
            )
            .subscribe({
                next: () => {
                    this.showLicenseDeleteConfirmModal.set(false);
                    this.licensePendingDelete.set(null);
                    this.showLicenseDeleteSuccessModal.set(true);
                },
                error: (err: unknown) => {
                    Logger.error('SingleDealerComponent', 'License delete failed', err);
                },
            });
    }

    /**
     * @protected
     * @description Dismisses the license-delete success modal and performs a full document reload.
     * @returns void
     */
    protected closeLicenseDeleteSuccessModal(): void {
        this.showLicenseDeleteSuccessModal.set(false);
        this.document.defaultView?.location.reload();
    }

    /**
     * @private
     * @description Checks if the email address has changed in the payload
     * @param payload - The update payload
     * @returns boolean
     */
    private hasEmailChanged(payload: DealerEditSubmitPayload): boolean {
        const initial = normalizeString(payload.initialEmail).toLowerCase();
        const next = normalizeString(payload.emailAddress).toLowerCase();
        return !!next && next !== initial;
    }

    /**
     * @private
     * @description Builds and runs `PATCH /api/dealers/:userId` with `user` and/or `clientInformation`.
     * @param payload - The update payload
     * @param emailChanged - When true, email is excluded from the user block (Auth0 handles it)
     * @returns Observable that completes when there is nothing to save, or after the patch succeeds
     */
    private prepareDealerPatch(
        payload: DealerEditSubmitPayload,
        emailChanged: boolean,
    ): Observable<unknown> {
        const body = this.buildDealerPatchBody(payload, emailChanged);
        const hasChanges = Boolean(body.user || body.clientInformation);
        return hasChanges ? this.dealerService.patchDealer(payload.userId, body) : of(null);
    }

    /**
     * @private
     * @description Runs email-uniqueness check, Auth0 email change, and parallel user/dealer PATCH when the dealer email changed.
     * @param payload - Submitted dealer form payload
     * @param user$ - User partial update observable
     * @param dealer$ - Dealer client partial update observable
     * @param onComplete - Invoked with true after a successful email-change flow
     * @returns void
     */
    private handleEmailChangeFlow(
        payload: DealerEditSubmitPayload,
        dealerPatch$: Observable<unknown>,
        onComplete: (emailChanged: boolean) => void,
    ): void {
        this.usersService
            .isEmailOwnedByAnotherUser(payload.emailAddress, payload.userId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((inUse) => {
                    if (inUse) {
                        this.saveError.set('This email is already in use.');
                        return EMPTY;
                    }
                    return forkJoin({
                        auth0: this.usersService.initiateAuth0VerifiedEmailChange(
                            payload.userId,
                            payload.emailAddress,
                        ),
                        dealer: dealerPatch$,
                    });
                }),
            )
            .subscribe({
                next: () => onComplete(true),
                error: (err: unknown) => {
                    Logger.error('SingleDealerComponent', 'Update failed', err);
                    const message = this.getUpdateErrorMessage(err);
                    this.saveError.set(message);
                },
            });
    }

    /**
     * @private
     * @description Runs parallel user and dealer client updates when the email is unchanged.
     * @param user$ - User partial update observable
     * @param dealer$ - Dealer client partial update observable
     * @param onComplete - Invoked with false after a successful standard update
     * @returns void
     */
    private handleStandardUpdateFlow(
        dealerPatch$: Observable<unknown>,
        onComplete: (emailChanged: boolean) => void,
    ): void {
        dealerPatch$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => onComplete(false),
            error: (err: unknown) => {
                Logger.error('SingleDealerComponent', 'Update failed', err);
                const message = this.getUpdateErrorMessage(err);
                this.saveError.set(message);
            },
        });
    }

    /**
     * @private
     * @description Aligns the UI countdown with sessionStorage for the currently loaded dealer before opening the edit modal.
     * @returns void
     */
    private syncPasswordResetCooldownForCurrentDealer(): void {
        this.clearChangePasswordTimer();
        const storage = this.dealerCooldownStorage();
        const userId = normalizeString(this.dealerHydration().userId);
        this.dealerCooldownTimerUserId = null;
        const left =
            storage && userId ? dealerChangePasswordCooldownSecondsLeft(storage, userId) : 0;
        if (left > 0 && userId) {
            this.startChangePasswordTimer(left, userId);
        } else {
            this.changePasswordSecondsLeft.set(0);
        }
    }

    /**
     * @private
     * @description Returns sessionStorage in the browser for per-dealer cooldown keys, or null on the server.
     * @returns Storage instance or null
     */
    private dealerCooldownStorage(): Storage | null {
        return isPlatformBrowser(this.platformId) ? sessionStorage : null;
    }

    /**
     * @private
     * @description Starts a one-second interval that decrements the visible cooldown and clears storage when it reaches zero.
     * @param initialSeconds - Starting countdown value in seconds
     * @param cooldownUserId - Dealer Auth user id whose storage row should clear when the timer finishes
     * @returns void
     */
    private startChangePasswordTimer(initialSeconds: number, cooldownUserId?: string | null): void {
        this.clearChangePasswordTimer();
        this.dealerCooldownTimerUserId = cooldownUserId ?? null;
        this.changePasswordSecondsLeft.set(initialSeconds);
        this.changePasswordInterval = setInterval(() => {
            const next = this.changePasswordSecondsLeft() - 1;
            if (next <= 0) {
                this.changePasswordSecondsLeft.set(0);
                const storage = this.dealerCooldownStorage();
                const uid = this.dealerCooldownTimerUserId;
                if (storage && uid) {
                    clearDealerChangePasswordCooldown(storage, uid);
                }
                this.dealerCooldownTimerUserId = null;
                this.clearChangePasswordTimer();
                return;
            }
            this.changePasswordSecondsLeft.set(next);
        }, 1000);
    }

    /**
     * @private
     * @description Stops the change-password cooldown interval without mutating sessionStorage.
     * @returns void
     */
    private clearChangePasswordTimer(): void {
        if (this.changePasswordInterval !== null) {
            clearInterval(this.changePasswordInterval);
            this.changePasswordInterval = null;
        }
    }

    /**
     * @private
     * @description Initiates the dealer data loading process
     * @param clientInformationId - ID to fetch
     * @returns void
     */
    private loadDealer(clientInformationId: string): void {
        this.subscribeDealerLoad(clientInformationId);
    }

    /**
     * @private
     * @description Fetches dealer by client id, applies row data to signals, and invokes optional callbacks.
     * @param clientInformationId - Route or API client information id
     * @param callbacks - Optional success or refresh-failure hooks
     * @returns void
     */
    private subscribeDealerLoad(
        clientInformationId: string,
        callbacks?: DealerLoadCallbacks,
    ): void {
        this.dealerPageLoading.set(true);
        this.dealerService
            .getDealerByClientInformationId(clientInformationId)
            .pipe(
                switchMap((res) => {
                    const row = res.data?.[0];
                    if (!row) return throwError(() => new Error('No dealer returned'));
                    this.applyDealerRow(row, clientInformationId);
                    return of(undefined);
                }),
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.dealerPageLoading.set(false)),
            )
            .subscribe({
                next: () => callbacks?.onComplete?.(),
                error: (err: unknown) => {
                    Logger.error('SingleDealerComponent', 'Failed to load dealer', err);
                    callbacks?.onRefreshFailed?.();
                },
            });
    }

    /**
     * @private
     * @description Maps API dealer payload into `dealerHydration`, header fields, and triggers license load for the owner user.
     * @param d - Dealer row from the API
     * @param fallbackClientId - Client id to use when the payload omits it
     * @returns void
     */
    private applyDealerRow(d: ApiDealerInformation, fallbackClientId: string): void {
        const u = d.user;
        const c = d.clientInformation;
        const userMeta = u.metadata ?? {};
        const clientMeta = c.metadata ?? {};
        const contactPerson = normalizeString(
            clientMeta['contactPerson'] ?? userMeta['contactPerson'],
        );
        const clientInformationId = c.clientInformationId ?? fallbackClientId;

        this.dealerHydration.set({
            userId: u.userId,
            clientInformationId,
            initialEmail: normalizeString(u.emailAddress),
            userMetadata: u.metadata ?? null,
            firstName: normalizeString(u.firstName),
            lastName: normalizeString(u.lastName),
            contactPersonPhone: normalizeString(u.contactNumber),
            contactPerson,
            clientMetadata: c.metadata ?? null,
            businessName: normalizeString(c.businessName),
            dealerAlias: normalizeString(c.alias),
            dealerId: clientInformationId,
            city: normalizeString(c.city),
            state: normalizeString(c.state),
            region: normalizeString(c.region),
            username: u.username,
            auth0UserId: u.auth0UserId,
            statusId: u.statusId,
            organizationIdSignin: u.organizationIdSignin,
            profilePicture: u.profilePicture ?? null,
        });

        this.headerBusinessName.set(normalizeString(c.businessName) || '—');
        this.headerAlias.set(c.alias ? `Dealer Alias: ${c.alias}` : '');

        const city = normalizeString(c.city);
        const state = normalizeString(c.state);
        const region = normalizeString(c.region);
        const cityState = [city, state].filter(Boolean).join(', ');
        this.headerLocation.set(
            cityState && region ? `${cityState} · ${region}` : cityState || region || '',
        );

        const ownerId = normalizeString(u.userId);
        if (ownerId) {
            this.dealerOwnerUserId.set(ownerId);
            this.licenseSearch.set('');
            this.licenseCurrentPage.set(1);
            this.loadLicenses(ownerId);
            this.syncPasswordResetCooldownForCurrentDealer();
        } else {
            this.dealerOwnerUserId.set(null);
            this.licenseRows.set([]);
            this.dealerLicenseTotalCount.set(0);
            this.licenseTableTotalCount.set(0);
            this.licenseCurrentPage.set(1);
            Logger.error('SingleDealerComponent', 'Missing userId for license load');
        }
    }

    /**
     * @private
     * @description Loads licenses for the dealer owner via GET /api/licenses with `ownerId` and optional search.
     * @param userId - Dealer owner user id (`ownerId` filter on the licenses API)
     * @param searchTerm - Optional search string matched against license key and alias
     * @returns void
     */
    private loadLicenses(
        userId: string,
        searchTerm = '',
        options?: { page?: number; append?: boolean },
    ): void {
        const term = searchTerm.trim();
        const page = options?.page ?? 1;
        const append = options?.append ?? false;

        if (append) {
            this.licensesLoadingMore.set(true);
        } else {
            this.licensesLoading.set(true);
        }

        this.licensesService
            .getAll({
                filters: { ownerId: userId },
                search: term || undefined,
                searchFields: term ? SINGLE_DEALER_LICENSE_SEARCH_FIELDS.join(',') : undefined,
                page,
                limit: SINGLE_DEALER_LICENSE_PAGE_SIZE,
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    if (append) {
                        this.licensesLoadingMore.set(false);
                    } else {
                        this.licensesLoading.set(false);
                    }
                }),
            )
            .subscribe({
                next: (res) => {
                    const mapped = (res.data ?? []).map((l) => this.licensesService.mapToUI(l));
                    const totalCount = res.meta?.pagination?.totalCount ?? mapped.length;

                    if (append) {
                        this.licenseRows.update((current) => [...current, ...mapped]);
                    } else {
                        this.licenseRows.set(mapped);
                    }

                    this.licenseCurrentPage.set(res.meta?.pagination?.page ?? page);
                    this.licenseTableTotalCount.set(totalCount);

                    if (!term) {
                        this.dealerLicenseTotalCount.set(totalCount);
                    }
                },
                error: (err: unknown) => {
                    Logger.error('SingleDealerComponent', 'Failed to load licenses', err);
                    if (!append) {
                        this.licenseRows.set([]);
                        this.licenseTableTotalCount.set(0);
                        if (!term) {
                            this.dealerLicenseTotalCount.set(0);
                        }
                    }
                },
            });
    }

    /**
     * @private
     * @description Builds a minimal `UpdateUserDto` from the edit payload compared against current hydration.
     * @param payload - Submitted dealer form payload
     * @param emailChanged - When true, email field is excluded from this delta (handled by Auth0 flow)
     * @param mergedMeta - User metadata only (for comparison with previous user metadata)
     * @returns Patch object sent to `UsersService.update`
     */
    private buildUserPatchDelta(
        payload: DealerEditSubmitPayload,
        emailChanged: boolean,
        mergedMeta: MetadataRecord,
    ): UpdateUserDto {
        const prev = this.dealerHydration();
        const delta: UpdateUserDto = {};

        if (normalizeString(payload.firstName) !== normalizeString(prev.firstName)) {
            delta.firstName = payload.firstName.trim();
        }
        if (normalizeString(payload.lastName) !== normalizeString(prev.lastName)) {
            delta.lastName = payload.lastName.trim();
        }
        if (
            normalizeString(payload.contactPersonPhone) !== normalizeString(prev.contactPersonPhone)
        ) {
            delta.contactNumber = payload.contactPersonPhone.trim() || undefined;
        }

        if (!emailChanged) {
            const nextEm = payload.emailAddress.trim();
            if (nextEm.toLowerCase() !== normalizeString(prev.initialEmail).toLowerCase()) {
                delta.emailAddress = nextEm;
            }
        }

        const prevMeta: MetadataRecord = {
            ...(prev.userMetadata ?? {}),
        };
        if (!isEqual(mergedMeta, prevMeta)) {
            delta.metadata = mergedMeta;
        }

        if (normalizeString(payload.username) !== normalizeString(prev.username)) {
            const t = payload.username?.trim();
            if (t) delta.username = t;
        }

        if (payload.statusId !== undefined && payload.statusId !== prev.statusId) {
            delta.statusId = payload.statusId;
        }

        if (payload.profilePicture !== prev.profilePicture) {
            delta.profilePicture = payload.profilePicture ?? undefined;
        }

        return delta;
    }

    /**
     * @private
     * @description Assembles the dealer PATCH body. When user fields changed, includes a full `user`
     * object (current form values) so the BFF applies profile updates together with client info.
     * @param payload - Submitted dealer form payload
     * @param emailChanged - When true, email is left unchanged in the user block
     * @returns Body for `DealerService.patchDealer`
     */
    private buildDealerPatchBody(
        payload: DealerEditSubmitPayload,
        emailChanged: boolean,
    ): DealerPatchBody {
        const mergedMeta: MetadataRecord = {
            ...(payload.userMetadata ?? {}),
        };
        const userDelta = this.buildUserPatchDelta(payload, emailChanged, mergedMeta);
        const clientDelta = this.buildDealerClientPatchDelta(payload);
        const body: DealerPatchBody = {};
        const hasUserChanges = Object.keys(userDelta).length > 0;
        const hasClientChanges = Object.keys(clientDelta).length > 0;

        // BFF expects a `user` object whenever dealer profile fields are saved (including client-only edits).
        if (hasUserChanges || hasClientChanges) {
            body.user = this.buildDealerPatchUserBlock(
                payload,
                emailChanged,
                mergedMeta,
                userDelta,
            );
        }
        if (hasClientChanges) {
            body.clientInformation = clientDelta;
        }

        return body;
    }

    /**
     * @private
     * @description Builds the `user` block for dealer PATCH using submitted values and hydration fallbacks.
     */
    private buildDealerPatchUserBlock(
        payload: DealerEditSubmitPayload,
        emailChanged: boolean,
        mergedMeta: MetadataRecord,
        userDelta: UpdateUserDto,
    ): DealerPatchUserBody {
        const prev = this.dealerHydration();
        const email =
            userDelta.emailAddress ??
            (emailChanged ? prev.initialEmail : payload.emailAddress.trim() || prev.initialEmail);

        return {
            userId: payload.userId,
            username: userDelta.username ?? payload.username ?? prev.username,
            emailAddress: email,
            firstName: userDelta.firstName ?? (payload.firstName.trim() || prev.firstName),
            lastName: userDelta.lastName ?? (payload.lastName.trim() || prev.lastName),
            contactNumber:
                userDelta.contactNumber ??
                (payload.contactPersonPhone.trim() || prev.contactPersonPhone),
            profilePicture:
                userDelta.profilePicture ??
                payload.profilePicture ??
                prev.profilePicture ??
                undefined,
            auth0UserId: payload.auth0UserId ?? prev.auth0UserId ?? '',
            statusId: userDelta.statusId ?? payload.statusId ?? prev.statusId,
            metadata: userDelta.metadata ?? mergedMeta,
            organizationIdSignin: payload.organizationIdSignin ?? prev.organizationIdSignin,
        };
    }

    /**
     * @private
     * @description Builds a partial `ApiClientInformation` patch from the edit payload versus hydration.
     * @param payload - Submitted dealer form payload
     * @returns Patch object sent to `DealerService.updateDealerClientInfo`
     */
    private buildDealerClientPatchDelta(
        payload: DealerEditSubmitPayload,
    ): Partial<ApiClientInformation> {
        const prev = this.dealerHydration();
        const patch: Partial<ApiClientInformation> = {};

        if (normalizeString(payload.businessName) !== normalizeString(prev.businessName)) {
            patch.businessName = payload.businessName.trim();
        }
        if (normalizeString(payload.dealerAlias) !== normalizeString(prev.dealerAlias)) {
            patch.alias = payload.dealerAlias.trim();
        }

        const nextCity = citySelectionKeyToApiCityName(payload.city);
        if (normalizeString(nextCity) !== normalizeString(prev.city)) {
            patch.city = nextCity;
        }
        if (normalizeString(payload.state) !== normalizeString(prev.state)) {
            patch.state = payload.state.trim();
        }
        if (normalizeString(payload.region) !== normalizeString(prev.region)) {
            patch.region = payload.region.trim();
        }

        if (normalizeString(payload.contactPerson) !== normalizeString(prev.contactPerson)) {
            patch.metadata = {
                ...(prev.clientMetadata ?? {}),
                contactPerson: payload.contactPerson.trim(),
            };
        }

        return patch;
    }

    /**
     * @private
     * @description Maps server errors to user-friendly messages for update operations.
     * @param err - The error object from the server
     * @returns string
     */
    private getUpdateErrorMessage(err: unknown): string {
        if (err instanceof HttpErrorResponse) {
            // Check for the specific 404 error where the BFF route is missing
            if (err.status === 404) {
                const body = err.error as { errors?: string[] };
                const firstError = body?.errors?.[0] || '';
                if (
                    firstError.includes('Cannot POST') &&
                    firstError.includes('auth0-verified-email-change')
                ) {
                    return 'Email update service is currently unavailable. Please contact support.';
                }
            }

            // Standard error message retrieval
            const serverMessage = (err.error as { message?: string })?.message;
            if (serverMessage) return serverMessage;
        }

        return 'Could not save changes.';
    }
}
